










import { Component, Vue } from 'vue-property-decorator';
import { ICacheService } from '@/services/iCacheService';
import { ObjectFactory } from '@/config/objectFactory';
import { MedTransGoConstants } from './util/constants';
import { DateUtils } from './util/dateUtils';
import './assets/css/Common.scss';
import { ApiRequest } from './types/request';
import { MemberProfile } from './types/response';
import Loader from './components/Loader.vue';
import SessionTimeout from './components/SessionTimeout.vue';
import SessionExpiredModal from './components/SessionExpiredModal.vue';
import { IUNCService } from '@/services/iUNCService';
@Component({
    components: {
        Loader,
        SessionTimeout,
        SessionExpiredModal,
    },
})
export default class App extends Vue {
    private show: any = false;
    private IDLE_TIMEOUT = 15; // Minutes
    private idleMinutesCounter = 0;
    private showTimeoutPop = false;
    private showSessionExpiredPop = false;
    private uncService: IUNCService = ObjectFactory.getUNCService();
    // Lifecycle hook. Check if user is logged in
    // If not then redirect to login page
    public async mounted() {
        // Handle UNC SSO first
        if (this.uncService.isUNCSubdomain()) {
            if (this.uncService.hasUNCCallback()) {
                // User is coming back from UNC login
                if (this.$route.path !== MedTransGoConstants.URL_UNC_CALLBACK) {
                    this.$router.push(MedTransGoConstants.URL_UNC_CALLBACK);
                }
                this.show = true;
                return;
            } else if (!this.isPublicRoute()) {
                // User is on UNC subdomain but no token - check if they need to login
                const iCacheService: ICacheService = ObjectFactory.getCacheService();
                const idToken = iCacheService.getValue(
                    MedTransGoConstants.FIELD_NAME_TOKEN,
                );

                if (!idToken) {
                    // No token - initiate UNC login
                    try {
                        await this.uncService.initiateLogin();
                        return;
                    } catch (error) {
                        this.redirectToLogin();
                        return;
                    }
                }
                // If they have a token, continue with normal flow below
            }
        }
        if (!this.isPublicRoute()) {
            const iCacheService: ICacheService = ObjectFactory.getCacheService();
            let idToken = '';
            let state = '';
            if (this.$route.query.id_token) {
                idToken = this.$route.query.id_token.toString();
                iCacheService.saveValue(MedTransGoConstants.FIELD_NAME_TOKEN, idToken);
                state = iCacheService.getSessionValue(MedTransGoConstants.FIELD_STATE);
            } else {
                idToken = iCacheService.getValue(MedTransGoConstants.FIELD_NAME_TOKEN);
            }
            // Redirect to login page if token is expired
            if (idToken) {
                const jsonPayload = iCacheService.getSessionInfo();
                const currentTimeIn = Math.floor(Date.now() / 1000);
                const tokenExpireTime = jsonPayload.exp; // exp is time in seconds
                // Token is expired
                this.setRefreshTokenTask();
                this.startIdleTimeCheck();
                this.startTokenExpirationCheck();
                this.checkTokenExpiration();
                if (jsonPayload.status === MedTransGoConstants.STATUS_SETUP_PENDING) {
                    this.show = true;
                    if (!this.$route.fullPath.startsWith(MedTransGoConstants.URL_ACCOUNT_SETUP)) {
                        const redirectTo = `${MedTransGoConstants.URL_ACCOUNT_SETUP}/${MedTransGoConstants.URL_MEMBER_TYPE}`;
                        this.$router.push(redirectTo);
                    }
                } else if (this.$route.fullPath.startsWith(MedTransGoConstants.URL_ACCOUNT_SETUP)) {
                    this.show = true;
                    if (jsonPayload.status !== MedTransGoConstants.STATUS_SETUP_PENDING) {
                        this.$router.push(MedTransGoConstants.URL_REQUESTS);
                    }
                } else if (jsonPayload.status === MedTransGoConstants.STATUS_PENDING_VERIFICATION) {
                    this.show = true;
                    if (!this.$route.fullPath.startsWith(MedTransGoConstants.URL_PENDING_VERIFICATION)) {
                        this.$router.push(MedTransGoConstants.URL_PENDING_VERIFICATION);
                    }
                } else {
                    const iHttpService = ObjectFactory.getHttpService();
                    const userId = ObjectFactory.getCacheService().getSessionInfo().id;
                    const apiRequest: ApiRequest = {
                        baseURL: this.$store.state.serverUrl,
                        url: `${MedTransGoConstants.HTTP_URL_MEMBERS}/${userId}`,
                    };
                    const response = await iHttpService.get<MemberProfile>(apiRequest);
                    if (response.success) {
                        ObjectFactory.getCacheService().saveValue(MedTransGoConstants.FIELD_MEMBER_PROFILE,
                            response.data);
                        if (response.data && response.data.isRestrictedView !== undefined) {
                            const blockedUrls = this.getBlockUrls(response.data.isRestrictedView);
                            this.$store.commit('setBlockedUrls', blockedUrls);
                        }
                    }
                    if (state) {
                        this.$router.push(state);
                        iCacheService.removeSessionValue(MedTransGoConstants.FIELD_STATE);
                    }
                    this.show = true;
                }
            } else {
                this.redirectToLogin();
            }
        } else {
            this.show = true;
        }
    }

    private redirectToLogin() {
        ObjectFactory.getCacheService().clearCache();
        ObjectFactory.getCacheService().saveSessionValue(MedTransGoConstants.FIELD_STATE, location.pathname);
        if (this.uncService.isUNCSubdomain()) {
            // For UNC users, redirect to home
            location.href = '/';
        } else {
            // Regular login
            const redirectUrl = `${this.$store.state.serverUrl}${MedTransGoConstants.HTTP_URL_LOGIN}?clientId=${this.$store.state.clientId}`;
            location.href = redirectUrl;
        }
    }

    private isPublicRoute(): boolean {
        return this.$store.state.publicUrl.indexOf(this.$route.path) >= 0 || this.$route.path.startsWith('/draft')
            || this.$route.path.startsWith('/track') || this.$route.path.startsWith(MedTransGoConstants.URL_PATIENT_PAY)
            || this.$route.path.startsWith(MedTransGoConstants.URL_PRIVATE_PAY);
    }

    private async setRefreshTokenTask() {
        const jsonPayload = ObjectFactory.getCacheService().getSessionInfo();
        const currentTimeInMillis = DateUtils.getCurrentTimeInMillis();
        const tokenExpireTime = jsonPayload.exp * 1000; // exp is time in seconds
        let refreshTokenTimeout = tokenExpireTime - currentTimeInMillis;
        const timeoutOffset = DateUtils.convertMinutesToMillis(2);
        refreshTokenTimeout = refreshTokenTimeout - (timeoutOffset <= refreshTokenTimeout ? timeoutOffset : 0);
        if (refreshTokenTimeout <= 0) {
            this.redirectToLogin();
        } else {
            setTimeout(() => {
                this.refreshToken();
            }, refreshTokenTimeout);
        }
    }

    private async refreshToken() {
        const clientId = this.$store.getters.SSOclientId;
        const response = await ObjectFactory.getUserService()
            .refreshToken(clientId, this.$store.state.serverUrl);
        if (response.success) {
            ObjectFactory.getCacheService().saveValue(MedTransGoConstants.FIELD_NAME_TOKEN, response.data);
            this.setRefreshTokenTask();
        } else {
            this.redirectToLogin();
        }
    }

    private startIdleTimeCheck() {
        document.onclick = () => {
            this.idleMinutesCounter = 0;
        };
        document.onmousemove = () => {
            this.idleMinutesCounter = 0;
        };
        document.onkeypress = () => {
            this.idleMinutesCounter = 0;
        };
        window.setInterval(this.checkIdleTime, 1000 * 60);
    }

    private startTokenExpirationCheck() {
        const jsonPayload = ObjectFactory.getCacheService().getSessionInfo();
        const currentTimeInMillis = DateUtils.getCurrentTimeInMillis();
        const tokenExpireTime = jsonPayload.exp * 1000; // exp is time in seconds
        window.setInterval(this.checkTokenExpiration, 1000 * 10);
    }

    private checkIdleTime() {
        this.idleMinutesCounter++;
        if (this.IDLE_TIMEOUT <= this.idleMinutesCounter) {
            this.showTimeoutPop = true;
        }
    }

    private checkTokenExpiration() {
        const jsonPayload = ObjectFactory.getCacheService().getSessionInfo();
        const currentTimeIn = Math.floor(Date.now() / 1000);
        const tokenExpireTime = jsonPayload.exp; // exp is time in seconds
        if (currentTimeIn > tokenExpireTime) {
            this.showSessionExpiredPop = true;
        }
    }

    private getBlockUrls(isMemberallowed: boolean): string[] {
        if (isMemberallowed) {
            return ['users?userType=patient', '/users/patients/add'];
        } else {
            return [];
        }
    }
}
